import { Box, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';

import type { GenericDataType } from '@/components/charts/charts.types';
import HorizontalBarChart from '@/components/charts/HorizontalBarChart';
import { REPORTS_COLORS } from '@/constants/chartcolors.constants';
import REPORT_PARAGRAPHS from '@/constants/reportparagraph.constants';
import ReportParagraph from '@/features/ReportsTemplateParagraph/ReportsParagraph';
import { ReportsTemplateSectionTitle } from '@/features/ReportsTemplateSectionTitle/ReportsTemplateSectionTitle';
import { REPORTS_NETWORK_EVENTS_HORIZONTAL_BAR_SERIES } from '@/pages/ReportsTemplates/ReportsTemplates.constants';

import useStyles from '../ReportsPages.styles';
import { useFetchReportsEventsMetricsData } from './EventsPages.hooks';

const EventsPage5 = () => {
  const { classes } = useStyles();

  // Fetch data for network events metrics
  const { data: networkEventsSourceIps } = useFetchReportsEventsMetricsData({
    metricField: 'source_ips',
    eventType: 'network'
  });
  const { data: networkEventsSourceAndDestinationIps } =
    useFetchReportsEventsMetricsData({
      metricField: 'source_and_destination_ips',
      eventType: 'network'
    });

  // Network Events by Source IPs by meta_direction "inbound"
  const networkEventsSourceIpsInboundData = useMemo(() => {
    const groupTopData = (networkEventsSourceIps ?? []).reduce(
      (acc, { count, source_ip, meta_direction }) => {
        if (meta_direction !== 'inbound') return acc;

        if (!acc[source_ip]) {
          acc[source_ip] = { name: source_ip, data: [0] };
        }
        (acc[source_ip].data as number[])[0] += count;
        return acc;
      },
      {} as GenericDataType
    );

    const sortedTop = Object.values(groupTopData)
      .sort((a, b) => b.data[0] - a.data[0])
      .slice(0, 10);

    return sortedTop.map((item) => ({
      ...item,
      count: item.data[0],
      color: REPORTS_COLORS.NETWORK_EVENTS
    }));
  }, [networkEventsSourceIps]);

  // Network Events by Source IPs by meta_direction "outbound"
  const networkEventsSourceIpsOutboundData = useMemo(() => {
    const groupTopData = (networkEventsSourceIps ?? []).reduce(
      (acc, { count, source_ip, meta_direction }) => {
        if (meta_direction !== 'outbound') return acc;

        if (!acc[source_ip]) {
          acc[source_ip] = { name: source_ip, data: [0] };
        }
        (acc[source_ip].data as number[])[0] += count;
        return acc;
      },
      {} as GenericDataType
    );

    const sortedTop = Object.values(groupTopData)
      .sort((a, b) => b.data[0] - a.data[0])
      .slice(0, 10);

    return sortedTop.map((item) => ({
      ...item,
      count: item.data[0],
      color: REPORTS_COLORS.NETWORK_EVENTS
    }));
  }, [networkEventsSourceIps]);

  // Network Events by Source IPs
  const networkEventsSourceIpsData = useMemo(() => {
    const groupTopData = (networkEventsSourceIps ?? []).reduce(
      (acc, { count, source_ip }) => {
        if (!acc[source_ip]) {
          acc[source_ip] = { name: source_ip, data: [0] };
        }
        (acc[source_ip].data as number[])[0] += count;
        return acc;
      },
      {} as GenericDataType
    );

    const sortedTop = Object.values(groupTopData)
      .sort((a, b) => b.data[0] - a.data[0])
      .slice(0, 10);

    return sortedTop.map((item) => ({
      ...item,
      count: item.data[0],
      color: REPORTS_COLORS.NETWORK_EVENTS
    }));
  }, [networkEventsSourceIps]);

  // Network Events by Source and Destination IPs
  const networkEventsSourceAndDestinationIpsData = useMemo(() => {
    const groupTopData = (networkEventsSourceAndDestinationIps ?? []).reduce(
      (acc, { count, source_ip, destination_ip }) => {
        const key = `${source_ip} -> ${destination_ip}`;
        if (!acc[key]) {
          acc[key] = { name: key, data: [0] };
        }

        (acc[key].data as number[])[0] += count;
        return acc;
      },
      {} as GenericDataType
    );

    const sortedTop = Object.values(groupTopData)
      .sort((a, b) => b.data[0] - a.data[0])
      .slice(0, 10);

    return sortedTop.map((item) => ({
      ...item,
      count: item.data[0],
      color: REPORTS_COLORS.NETWORK_EVENTS
    }));
  }, [networkEventsSourceAndDestinationIps]);

  return (
    <div className={classes.reportsPageContainer}>
      <ReportsTemplateSectionTitle>Network Events</ReportsTemplateSectionTitle>
      <Box sx={{ flexDirection: 'row', display: 'flex', gap: 2 }}>
        <Box sx={{ width: '49%' }}>
          <Stack spacing={2}>
            <div
              data-testid="network-events-source-ips-inbound"
              style={{ height: 300 }}
            >
              <Typography className={classes.reportsPreviewSubTitle}>
                Top Windows Events
              </Typography>
              <HorizontalBarChart
                height={275}
                data={networkEventsSourceIpsInboundData}
                series={REPORTS_NETWORK_EVENTS_HORIZONTAL_BAR_SERIES}
              />
            </div>
            <ReportParagraph fontSize="12px">
              {REPORT_PARAGRAPHS.topInboundSourceIPsDescription}
            </ReportParagraph>
            <div style={{ height: 20 }} />
            <div
              data-testid="network-events-source-ips"
              style={{ height: 300 }}
            >
              <Typography className={classes.reportsPreviewSubTitle}>
                Top Source IPs
              </Typography>
              <HorizontalBarChart
                height={275}
                data={networkEventsSourceIpsData}
                series={REPORTS_NETWORK_EVENTS_HORIZONTAL_BAR_SERIES}
              />
            </div>
            <ReportParagraph fontSize="12px">
              {REPORT_PARAGRAPHS.topSourceIPsDescription}
            </ReportParagraph>
          </Stack>
        </Box>
        <div style={{ width: '2%' }}>
          <Box
            sx={{
              backgroundColor: '#D1E2D5',
              width: 2,
              height: '100%'
            }}
          />
        </div>
        <Box sx={{ width: '49%' }}>
          <Stack spacing={2}>
            <div
              data-testid="network-events-source-ips-outbound"
              style={{ height: 300 }}
            >
              <Typography className={classes.reportsPreviewSubTitle}>
                Top Outbound Source IPs
              </Typography>
              <HorizontalBarChart
                height={275}
                data={networkEventsSourceIpsOutboundData}
                series={REPORTS_NETWORK_EVENTS_HORIZONTAL_BAR_SERIES}
              />
            </div>
            <ReportParagraph fontSize="12px">
              {REPORT_PARAGRAPHS.topOutboundSourceIPsDescription}
            </ReportParagraph>
            <div style={{ height: 20 }} />
            <div
              data-testid="network-events-source-and-destination-ips"
              style={{ height: 300 }}
            >
              <Typography className={classes.reportsPreviewSubTitle}>
                Top Source And Destination IPs
              </Typography>
              <HorizontalBarChart
                height={275}
                data={networkEventsSourceAndDestinationIpsData}
                series={REPORTS_NETWORK_EVENTS_HORIZONTAL_BAR_SERIES}
              />
            </div>
            <ReportParagraph fontSize="12px">
              {REPORT_PARAGRAPHS.topSourceAndDestinationIPsDescription}
            </ReportParagraph>
          </Stack>
        </Box>
      </Box>
    </div>
  );
};

export default EventsPage5;
